<template>
  <BasePageTemplate :title="verificationCodeTitleString" :description="verificationCodeDescriptionComputedString" v-model:error="error">
    <DynamicForm v-model:data="data" :definition="formDefinition" :form-id="formId">
      <!-- Otp field -->
      <Field :label="verificationCodeString" is-required target="otp" class-container="flex flex-col align-center">
        <InputOtp
          :ref="otpInputId"
          v-model="data.otp"
          class="justify-center"
          fluid
          integer-only
          :length="6"
          :disabled="disabled"
          @enter="onOtpClickAsync"
        />
      </Field>

      <MainButton :label="nextString" :loading="disabled" :disabled="disabled" @click="onResetCredentialsClickAsync" />
    </DynamicForm>

    <!-- Other buttons -->
    <div class="flex align-center justify-center">
      <Button
        icon="chevron_left"
        rounded
        outlined
        severity="secondary"
        :disabled="disabled"
        :aria-label="backString"
        v-tooltip.top="{ value: backString, pt: { text: 'dark' } }"
        @click="onBackClick"
      ></Button>
    </div>
  </BasePageTemplate>
</template>

<script setup lang="ts">
import { AuthenticationApiService } from "@authentication/services";
import { OtpResetCredentialsResponseType, OtpResetCredentialsType } from "@authentication/types";
import { BasePageTemplate, PasswordInput } from "@authentication/ui";
import { IFormDefinition } from "@common/core";
import { useForm, useProblemDetailsUtils } from "@common/hooks";
import { ProblemDetailsType } from "@common/types";
import { Button, DynamicForm, Field, MainButton } from "@common/ui";
import { useTranslation } from "i18next-vue";
import InputOtp from "primevue/inputotp";
import { computed, nextTick, onMounted, ref, useId, useTemplateRef } from "vue";
import * as yup from "yup";

// Models
const data = defineModel<OtpResetCredentialsType>("data", { required: true });
const otpMethod = defineModel<OtpResetCredentialsResponseType>("method", { required: true });

const emit = defineEmits<{
  (e: "back"): void;
  (e: "next"): void;
}>();

// Constants
const formId = useId();
const emailInputId = useId();
const otpInputId = useId();

// Variables
const { t } = useTranslation();
const problemDetailUtils = useProblemDetailsUtils();
const dataFrom = useForm([formId]);
const emailInput = useTemplateRef<InstanceType<typeof PasswordInput>>(emailInputId);
const otpInput = useTemplateRef<InstanceType<typeof PasswordInput>>(otpInputId);
const disabled = ref(false);
const error = ref<ProblemDetailsType | null>();

// Strings
const backString = t("commons.back");
const nextString = t("commons.next");
const preResetValidationErrorString = t("reset_credentials.otp_reset_invalid_error");
const verificationCodeString = t("reset_credentials.verification_code");
const verificationEOtpCodeDescriptionString = t("reset_credentials.verification_eotp_code_description", { email: data.value.email });
const verificationSOtpCodeDescriptionString = t("reset_credentials.verification_sotp_code_description", { hint: otpMethod.value.hint });
const verificationCodeTitleString = t("reset_credentials.verification_code_title");

// Computed
const verificationCodeDescriptionComputedString = computed(() => {
  return otpMethod.value.method === "E-OTP" ? verificationEOtpCodeDescriptionString : verificationSOtpCodeDescriptionString;
});

// Forms
const formDefinition = {
  username: {
    spec: yup.string().required().min(3).label(verificationCodeString),
  },
  otp: {
    spec: yup.string().required().length(6).label(verificationCodeString),
  },
} as IFormDefinition;

// Methods
const focusOtpInputAsync = async () => {
  await nextTick(() => {
    const el = otpInput.value?.$el as HTMLDivElement | null;
    if (el) {
      el.getElementsByTagName("input")[0].focus();
    }
  });
};

const onBackClick = () => {
  emit("back");
};

const onResetCredentialsClickAsync = async () => {
  disabled.value = true;
  error.value = null;

  dataFrom.cleanErrors();

  if (!(await dataFrom.isValidAsync())) {
    disabled.value = false;
    await focusOtpInputAsync();
    return;
  }

  AuthenticationApiService.authResetCredentialsAsync(data.value)
    .then((_) => {
      emit("next");
    })
    .catch((_) => {
      setErrorMessage(preResetValidationErrorString);
    })
    .finally(() => {
      disabled.value = false;
    });
};

const onOtpClickAsync = async () => {
  await nextTick(() => {
    emailInput.value?.$el.focus();
  });
};

const setErrorMessage = (message: string) => {
  error.value = problemDetailUtils.fromString(message);
};

// Hooks
onMounted(async () => {
  await focusOtpInputAsync();
});
</script>
